import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';

import './BusinessAppDetail.css';

import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import IconPreview from '@/component/common/layout/gNav/icon/IconPreview';
import { getPublicAPI } from '@/service/publicService';
import localStorageUtil from '@/common/utils/localStorageUtil';
import LoadingPanel from '@/component/common/LoadingPanel';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import { buildShareLinkBusinessApp } from '../handler/PanelManagementHandler';
import MuiTooltip from '@/component/common/MuiTooltip';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import useHtmlRuntimeBridge from '@/component/htmlTranslation/useHtmlRuntimeBridge';
import HtmlOnDemandToggle from '@/component/htmlTranslation/HtmlOnDemandToggle';
import { parseHtmlFieldKey } from '@/component/htmlTranslation/HtmlTranslationHandler';

function BusinessAppDetail(props) {
    // Re-render when the translation dictionary loads/updates.
    useTranslationSubscription();

    const { ticketId }  = props;

    const [appDetail, setAppDetail] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isShowPopUpReload, setIsShowPopUpReload] = useState(false);
    const iframeRef = useRef(null);

    // MLT-001-F3: appDisplaySrc is the translation copy when the BE decided this viewer gets it.
    const { fieldId: appFieldId } = parseHtmlFieldKey(appDetail?.appSrc);
    const { onIframeLoad, onDemand } = useHtmlRuntimeBridge({
        iframeRef,
        ticketId,
        fieldId: appFieldId,
        sourceLanguage: appDetail?.translationSourceLanguage,
        targetLanguage: appDetail?.translationTargetLanguage,
        iframeSrc: appDetail?.previewUrl
    });

    useEffect(() => {
        if (!ticketId) return;
        setIsLoading(true);

        let requestParams = {
            corporationInuseId: localStorageUtil.getAffiliationOrganization(),
            projectInuseId: localStorageUtil.getSelectedProjectId(),
            userId: localStorageUtil.getUserDetailId(),
            ticketId: ticketId,
            getApiType: Constant.GET_API_PUBLIC_TYPE.GET_TICKET_BUSINESS_APP_DETAIL
        };
        getPublicAPI(null, requestParams)
            .then(response => {
                const app = Utils.convertJSONDataToObject(response.payload);
                if (Utils.isEmptyObject(app)) {
                    setIsShowPopUpReload(true);
                    return;
                }
                if (!Utils.isEmptyObject(app && app.appSrc)) {
                    // appSrc stays the original (share link); the iframe loads appDisplaySrc.
                    const cdnUrl = `${process.env.REACT_APP_CLOUD_FRONT_URL}/${app.appDisplaySrc || app.appSrc}?v=${Date.now()}`;
                    setAppDetail({
                        ...app,
                        previewUrl: cdnUrl
                    })
                }
            })
            .catch(error => {
                console.log('✅ >>> error :', error);
                setIsShowPopUpReload(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [ticketId]);

    return (
        <>
            <LoadingPanel isLoading={isLoading} />
            <div className="business-app-detail-wrapper">
                <div className="business-app-detail-header">
                    <HtmlOnDemandToggle toggleId={`html-ondemand-translation-toggle-${ticketId}`} onDemand={onDemand} />
                    <MuiTooltip title={trI18n('OPEN_ON_NEXT_TAB')}>
                        <button
                            className="btn-reset-default app-item-open-view-app-wrapper"
                            onClick={() => {
                                if (!appDetail?.appSrc) return;
                                const shareLink = buildShareLinkBusinessApp(appDetail?.appSrc)
                                window.open(shareLink, '_blank')
                            }}
                        >
                            <IconPreview />
                        </button>
                    </MuiTooltip>
                </div>
                <div className="business-app-preview">
                    {appDetail?.previewUrl && (
                        <iframe
                            ref={iframeRef}
                            onLoad={onIframeLoad}
                            title="business-app-detail"
                            className="business-app-source"
                            style={{
                                width: '100%',
                                height: '99%',
                                minHeight: '600px',
                            }}
                            src={appDetail?.previewUrl}
                            allow={Constant.IFRAME_ALLOW_PERMISSIONS}
                            allowfullscreen
                        />
                    )}
                </div>
            </div>

            {/* Confirm */}
            <ConfirmDialog
                isOpen={isShowPopUpReload}
                msgConfirm={trI18n('COMMSG030')}
                okCallBack={() => {
                    setIsShowPopUpReload(false);
                    window.location.reload()
                }}
            />
        </>
    );
}

BusinessAppDetail.propTypes = {
    ticketId: PropTypes.string,
};

export default BusinessAppDetail;
